import canvg from "canvg-browser";
import type { Chart as ChartObject } from "highcharts";
import Highcharts from "highcharts";
import HighchartsMore from "highcharts/highcharts-more";
import HighchartsExporting from "highcharts/modules/exporting";
import HighchartsMaps from "highcharts/modules/map";
import HighchartsOfflineExporting from "highcharts/modules/offline-exporting";
import React from "react";

HighchartsMore(Highcharts);

// Enable exporting api for charts
HighchartsExporting(Highcharts);
HighchartsOfflineExporting(Highcharts);

// Enable geographic maps
HighchartsMaps(Highcharts);

interface ChartExportImageOptions {
  imageScale?: number;
  mimeType?: "image/jpeg" | "image/png";
  imageQuality?: number;
  shouldHideAxisLabels?: boolean;
  title?: Highcharts.TitleOptions;
}

export enum ChartType {
  PIE = "pie",
  BAR = "bar",
  LINE = "line",
  AREA_RANGE = "arearange",
}

interface Props {
  options?: Highcharts.Options;
  // Whether the chart should animate series between updates
  shouldAnimate?: boolean;
  uniqueChartId: string;
}

class Chart extends React.PureComponent<Props> {
  /* eslint-disable @typescript-eslint/member-ordering */
  private chart!: ChartObject;

  public static exportCallbacksByUniqueChartId: Record<
    string,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (options: ChartExportImageOptions) => string
  > = {};

  public static defaultProps = {
    shouldAnimate: true,
  };

  private constructor(props: Props) {
    super(props);
    Highcharts.setOptions({
      // The dates are already in UTC, high charts assumes they are local time and converts them to UTC
      global: {
        useUTC: false,
      },
      lang: {
        thousandsSep: ",",
      },
    });
  }

  public componentDidMount() {
    const { uniqueChartId, options } = this.props;

    this.chart = new Highcharts.Chart(uniqueChartId, options ?? {}, () => {
      Chart.exportCallbacksByUniqueChartId[uniqueChartId] =
        this.exportToImage.bind(this);
    });
  }

  public componentDidUpdate() {
    const { options, shouldAnimate } = this.props;

    this.chart.update(options ?? {}, true, true, shouldAnimate);
  }

  public componentWillUnmount() {
    const { uniqueChartId } = this.props;
    delete Chart.exportCallbacksByUniqueChartId[uniqueChartId];

    this.chart.destroy();
  }

  // Export the chart as a rendered image
  public exportToImage({
    imageScale = 0.5,
    mimeType = "image/jpeg",
    imageQuality = 0.7,
    shouldHideAxisLabels = true,
    title = undefined,
  }: ChartExportImageOptions = {}): string {
    const svg = this.chart.getSVG({
      chart: {
        events: {
          load() {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            if ((this.renderer as any).forExport) {
              // Workaround for axis options not updating properly for export
              // See: https://github.com/highcharts/highcharts/issues/13492
              if (shouldHideAxisLabels) {
                this.xAxis[0].update({ title: { text: null } });
                this.yAxis[0].update({ title: { text: null } });
              }
            }
          },
        },
      },
      exporting: {
        sourceHeight: this.chart.container.offsetHeight * imageScale,
        sourceWidth: this.chart.container.offsetWidth * imageScale,
      },
      legend: {
        enabled: this.chart.options.chart?.type === ChartType.LINE,
      },
      title,
    });
    const canvas = document.createElement("canvas");
    canvg(canvas, svg);
    return canvas.toDataURL(mimeType, imageQuality);
  }

  public render() {
    return null;
  }
}
export default Chart;
